import type { BaseAddress, MailingAddress } from '~/types/ecommerce';

type CurrencyDisplay = 'symbol' | 'code' | 'name' | 'none';

type CurrencySign = 'standard' | 'accounting';

type CurrencyPosition = 'before' | 'after';

type DecimalDisplay = 'always' | 'hideIfZero' | 'never';

interface FormatCurrencyOptions {
  /**
   * Currency symbol/code/name to display.
   *
   * Examples: "$", "USD", "US dollars", "€"
   */
  currency?: string;

  /**
   * Controls whether/how the currency is displayed.
   *
   * Similar intent to Intl.NumberFormat currencyDisplay.
   */
  currencyDisplay?: CurrencyDisplay;

  /**
   * Where to place the currency text.
   */
  currencyPosition?: CurrencyPosition;

  /**
   * Text between the currency and number.
   *
   * Useful for formats like "USD 1,234.50" or "1,234.50 USD".
   */
  currencySpacing?: string;

  /**
   * Thousands/group separator.
   */
  groupSeparator?: string;

  /**
   * Decimal separator.
   */
  decimalSeparator?: string;

  /**
   * Whether to use grouping separators.
   */
  useGrouping?: boolean;

  /**
   * Minimum fraction digits.
   *
   * Similar to Intl.NumberFormat minimumFractionDigits.
   */
  minimumFractionDigits?: number;

  /**
   * Maximum fraction digits.
   *
   * Similar to Intl.NumberFormat maximumFractionDigits.
   */
  maximumFractionDigits?: number;

  /**
   * Controls when decimals are displayed.
   *
   * - "always": show decimals using at least minimumFractionDigits
   * - "hideIfZero": hide decimals when the rounded decimal portion is zero
   * - "never": round to a whole number and hide decimals
   */
  decimalDisplay?: DecimalDisplay;

  /**
   * Keep a leading zero for values between -1 and 1.
   *
   * true: "$0.50"
   * false: "$.50"
   */
  leadingZero?: boolean;

  /**
   * Negative number display.
   *
   * standard: -$1,234.50
   * accounting: ($1,234.50)
   */
  currencySign?: CurrencySign;
}

const defaultCurrencyOptions: Required<FormatCurrencyOptions> = {
  currency: '$',
  currencyDisplay: 'symbol',
  currencyPosition: 'before',
  currencySpacing: '',
  groupSeparator: ',',
  decimalSeparator: '.',
  useGrouping: true,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  decimalDisplay: 'hideIfZero',
  leadingZero: true,
  currencySign: 'standard',
};

/**
 * Formats a finite number as a currency-like string without relying on `Intl.NumberFormat`.
 *
 * This helper is intended for runtimes where `Intl` may be unavailable or inconsistent. It
 * supports configurable grouping, decimal separators, currency placement, negative formatting,
 * and decimal display behavior.
 *
 * By default, whole-dollar values hide the decimal portion, while values with non-zero cents keep
 * the configured fraction digits.
 *
 * @param value - The numeric value to format.
 * @param options - Optional currency formatting options.
 *
 * @returns The formatted currency string, or `undefined` when `value` is not finite.
 *
 * @example
 * formatCurrency(1234)
 * // "$1,234"
 *
 * @example
 * formatCurrency(1234.5)
 * // "$1,234.50"
 *
 * @example
 * formatCurrency(1234, { decimalDisplay: 'always' })
 * // "$1,234.00"
 *
 * @example
 * formatCurrency(1234.56, { decimalDisplay: 'never' })
 * // "$1,235"
 *
 * @example
 * formatCurrency(1234.5, {
 *   currency: '€',
 *   currencyPosition: 'after',
 *   groupSeparator: '.',
 *   decimalSeparator: ',',
 * })
 * // "1.234,50€"
 *
 * @example
 * formatCurrency(-1234.5, { currencySign: 'accounting' })
 * // "($1,234.50)"
 */
const formatCurrency = (value: number, options: FormatCurrencyOptions = {}): string | undefined => {
  if (!Number.isFinite(value)) {
    return undefined;
  }

  const resolvedOptions = resolveCurrencyOptions(options);

  const {
    currency,
    currencyDisplay,
    currencyPosition,
    currencySpacing,
    groupSeparator,
    decimalSeparator,
    useGrouping,
    minimumFractionDigits,
    maximumFractionDigits,
    decimalDisplay,
    leadingZero,
    currencySign,
  } = resolvedOptions;

  const isNegative = value < 0;
  const absValue = Math.abs(value);

  const fractionDigits = decimalDisplay === 'never'
    ? 0
    : maximumFractionDigits;

  const rounded = absValue.toFixed(fractionDigits);
  let [integerPart = '0', fractionPart = ''] = rounded.split('.');

  if (useGrouping) {
    integerPart = addGrouping(integerPart, groupSeparator);
  }

  if (!leadingZero && integerPart === '0' && fractionPart.length > 0) {
    integerPart = '';
  }

  const showFraction = shouldShowFraction({
    decimalDisplay,
    fractionPart,
    minimumFractionDigits,
    maximumFractionDigits,
  });

  if (showFraction) {
    fractionPart = fractionPart.padEnd(minimumFractionDigits, '0');
  }

  const numberText = showFraction
    ? `${integerPart}${decimalSeparator}${fractionPart}`
    : integerPart;

  const currencyText = currencyDisplay === 'none'
    ? ''
    : currency;

  const unsignedText = joinCurrencyParts({
    numberText,
    currencyText,
    currencyPosition,
    currencySpacing,
  });

  if (!isNegative) {
    return unsignedText;
  }

  return currencySign === 'accounting'
    ? `(${unsignedText})`
    : `-${unsignedText}`;
};

const resolveCurrencyOptions = (
  options: FormatCurrencyOptions,
): Required<FormatCurrencyOptions> => {
  const resolved = {
    ...defaultCurrencyOptions,
    ...options,
  };

  const min = normalizeFractionDigits(resolved.minimumFractionDigits);
  const max = normalizeFractionDigits(resolved.maximumFractionDigits);

  return {
    ...resolved,
    minimumFractionDigits: Math.min(min, max),
    maximumFractionDigits: max,
  };
};

const normalizeFractionDigits = (value: number): number => {
  return Math.max(0, Math.min(20, Math.trunc(value)));
};

const addGrouping = (
  value: string,
  groupSeparator: string,
): string => {
  return value.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator);
};

const shouldShowFraction = ({
  decimalDisplay,
  fractionPart,
  minimumFractionDigits,
  maximumFractionDigits,
}: {
  decimalDisplay: DecimalDisplay;
  fractionPart: string;
  minimumFractionDigits: number;
  maximumFractionDigits: number;
}): boolean => {
  if (maximumFractionDigits <= 0 || decimalDisplay === 'never') {
    return false;
  }

  if (decimalDisplay === 'always') {
    return minimumFractionDigits > 0 || hasNonZeroFraction(fractionPart);
  }

  return hasNonZeroFraction(fractionPart);
};

const hasNonZeroFraction = (value: string): boolean => {
  return [...value].some(digit => digit !== '0');
};

const joinCurrencyParts = ({
  numberText,
  currencyText,
  currencyPosition,
  currencySpacing,
}: {
  numberText: string;
  currencyText: string;
  currencyPosition: CurrencyPosition;
  currencySpacing: string;
}): string => {
  if (!currencyText) {
    return numberText;
  }

  return currencyPosition === 'after'
    ? `${numberText}${currencySpacing}${currencyText}`
    : `${currencyText}${currencySpacing}${numberText}`;
};

/**
 * Formats a finite numeric value as a percentage string.
 *
 * The value is expected to already be in percentage units, so `25` formats as `"25%"`.
 *
 * @param value - The numeric percentage value to format.
 * @param fractionDigits - Number of digits to display after the decimal point.
 * @returns A percentage string, or `undefined` when `value` is not finite.
 *
 * @example
 * formatPercent(25)
 * // "25%"
 *
 * @example
 * formatPercent(25.5)
 * // "26%"
 *
 * @example
 * formatPercent(25.5, 1)
 * // "25.5%"
 */
export const formatPercent = (value: number, fractionDigits = 0): string | undefined => {
  if (!Number.isFinite(value)) {
    return undefined;
  }

  const digits = Math.max(0, Math.min(20, Math.trunc(fractionDigits)));

  return `${value.toFixed(digits)}%`;
};

/**
 * Create a pluralized string for a noun based on count.
 *
 * @param count - The numeric count
 * @param noun - The noun to pluralize
 * @param suffix - The suffix to append for plural (default: "s")
 * @returns String with count and noun, pluralized unless count is exactly 1
 *
 * @example
 * pluralize(0, "item") // "0 items"
 * pluralize(1, "item") // "1 item"
 * pluralize(3, "item") // "3 items"
 */
const pluralize = (count: number, noun: string, suffix = 's') =>
  `${count} ${noun}${count === 1 ? '' : suffix}`;

/**
 * Format a numeric rating to 2 decimal places, trimming trailing zero.
 *
 * @param value - Numeric rating
 * @returns Formatted rating string
 *
 * @example
 * formatRating(4.5) // "4.5"
 * formatRating(4.00) // "4"
 */
const formatRating = (value: number) => value.toFixed(2).replace(/0{1}$/, '');

/**
 * Formats a string as a standard US phone number.
 *
 * The input may contain punctuation, spaces, or a leading US country code.
 * A leading `1` is ignored when the cleaned value has 11 digits.
 *
 * @param value - Raw phone number string.
 * @returns A formatted phone number, or `undefined` when the value cannot be formatted.
 *
 * @example
 * formatPhoneNumber('5551234567')
 * // "(555) 123-4567"
 *
 * @example
 * formatPhoneNumber('1-555-123-4567')
 * // "(555) 123-4567"
 */
export const formatPhoneNumber = (value: string): string | undefined => {
  const digits = value.replace(/\D/g, '');
  const normalized = digits.length === 11 && digits.startsWith('1')
    ? digits.slice(1)
    : digits;

  if (normalized.length !== 10) {
    return undefined;
  }

  const areaCode = normalized.slice(0, 3);
  const prefix = normalized.slice(3, 6);
  const lineNumber = normalized.slice(6);

  return `(${areaCode}) ${prefix}-${lineNumber}`;
};

/**
 * Format a BaseAddress object into a single line string.
 *
 * @param address - Address object (ref or value)
 * @returns A formatted address string
 */
const formatAddress = (address: MailingAddress | BaseAddress) =>
  join(
    concat(
      ', ',
      address.street,
      address.street2,
      address.city,
      address.state,
    ),
    address.postcode,
  );

/**
 * Concatenate non-empty parts using a separator.
 *
 * @param separator - Separator string
 * @param parts - Strings to join
 * @returns Concatenated string
 */
const concat = (separator: string, ...parts: (string | null | undefined)[]) =>
  parts
    .filter(p => !!p?.length)
    .join(separator)
    .trim();

/**
 * Join non-empty parts with a single space.
 *
 * @param parts - Strings to join
 * @returns Concatenated string
 */
const join = (...parts: (string | null | undefined)[]) => concat(' ', ...parts);

/**
 * Mask all but the last `last` characters of a string.
 *
 * @param value - Original string
 * @param last - Number of characters to keep unmasked (default: 4)
 * @param char - Mask character (default: "X")
 * @returns Masked string
 *
 * @example
 * maskify("1234567890123456") // "XXXXXXXXXXXX3456"
 */
const maskify = (value: string, last = 4, char = 'X') =>
  value.slice(0, last * -1).replace(/./g, char) + value.slice(last * -1);

/**
 * Converts a block of plain text into HTML paragraphs.
 *
 * Consecutive blank lines create new paragraphs. Single line breaks within a
 * paragraph are converted to `<br>`.
 *
 * The input is HTML-escaped before paragraph tags are added.
 *
 * @param text - Raw plain text, which may include line breaks.
 * @returns HTML paragraphs, or an empty string when `text` is empty.
 *
 * @example
 * paragraphize('Line one\nLine two\n\nNext paragraph')
 * // "<p>Line one<br>Line two</p><p>Next paragraph</p>"
 */
export const paragraphize = (text?: string | null): string => {
  const normalized = text?.trim().replace(/\r\n?/g, '\n');

  if (!normalized) {
    return '';
  }

  return normalized
    .split(/\n\s*\n+/)
    .map(paragraph => paragraph.trim())
    .filter(Boolean)
    .map(paragraph => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br>')}</p>`)
    .join('');
};

const escapeHtml = (value: string): string => {
  return value.replace(/[&<>"']/g, char => htmlEscapes[char] ?? char);
};

const htmlEscapes: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  '\'': '&#39;',
};

/**
 * Provides common formatting utilities for currency, percent, rating, phone, address, etc.
 *
 * @returns An object with formatting methods
 */
export const useFormatting = () => {
  return {
    currency: formatCurrency,
    percent: formatPercent,
    rating: formatRating,
    phone: formatPhoneNumber,
    address: formatAddress,
    maskify,
    pluralize,
    paragraphize,
    concat,
    join,
  };
};
